import {Badge, TableRow, TableRowActionsData} from 'platform/components';
import {HStack, Show, Text, ThemeColorPath, VStack} from 'platform/foundation';
import {CurrencyFormatter, NumberFormatter} from 'platform/locale';

import {ReactNode} from 'react';

import {isNil} from 'ramda';

import {SalesLeagueBranchRow, SalesLeaguePeriod} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, RequiredTestIdProps, suffixTestId, useNavigate} from 'shared/web';

import {SalesLeagueCell} from '../../../components/SalesLeagueCell';
import {SalesLeagueRank} from '../../../components/SalesLeagueRank';
import {SalesLeagueRankChange} from '../../../components/SalesLeagueRankChange';
import {SalesLeagueVehiclesChange} from '../../../components/SalesLeagueVehiclesChange';
import {BRANCH_BOARD_ACTIONS_COLUMN_WIDTH} from '../../../constants/BRANCH_BOARD_COLUMN_WIDTHS';
import {buildBranchBoardDrillDownPath} from '../../../utils/buildBranchBoardDrillDownPath';
import {formatMarginAmount} from '../../../utils/formatMarginAmount';
import {parseRatio} from '../../../utils/parseRatio';
import {SalesLeagueBranchBadge} from './SalesLeagueBranchBadge';

const PODIUM_LAST_RANK = 3;
const LEADER_RANK = 1;
const VEHICLES_PER_REP_DECIMALS = 1;

const CURRENT_BRANCH_ROW_COLOR: ThemeColorPath = 'palettes.blue.10.100';
const ROW_HOVER_COLOR: ThemeColorPath = 'palettes.neutral.10.100';

interface SalesLeagueBranchBoardTableRowProps extends RequiredTestIdProps {
  row: SalesLeagueBranchRow;
  currency: string;
  marginColumnsVisible: boolean;
  period: SalesLeaguePeriod;
  formatCurrency: CurrencyFormatter;
  formatNumber: NumberFormatter;
}

export function SalesLeagueBranchBoardTableRow(props: SalesLeagueBranchBoardTableRowProps) {
  const navigate = useNavigate();

  const isEmphasised = props.row.rank <= PODIUM_LAST_RANK || props.row.isCurrentBranch;

  const vehiclesPerRep = parseRatio(props.row.vehiclesPerSeller);

  // Never `gapToLeader === 0`: two branches tied for the lead both carry zero while holding
  // ranks 1 and 2, so only the rank says which one leads.
  const isLeader = props.row.rank === LEADER_RANK;

  // `gapToLeader` is zero or negative, and the label supplies the sign, so the cell reads
  // the magnitude.
  const gapLabel = isLeader
    ? i18n.t('page.salesLeague.branchBoard.table.leads')
    : i18n.t('page.salesLeague.branchBoard.table.gapValue', {
        count: props.formatNumber(Math.abs(props.row.gapToLeader)) ?? EMPTY_PLACEHOLDER,
      });

  const actions: TableRowActionsData = {
    primary: [
      {
        icon: 'action/open_in_new',
        title: props.row.detailAvailable
          ? i18n.t('page.salesLeague.branchBoard.table.openBranch')
          : i18n.t('page.salesLeague.branchBoard.table.openBranchUnavailable'),
        isDisabled: !props.row.detailAvailable,
        onClick: () => navigate(buildBranchBoardDrillDownPath(props.row.branchId, props.period)),
      },
    ],
  };

  const cells: ReactNode[] = [
    <SalesLeagueCell key="rank">
      <SalesLeagueRank rank={props.row.rank} data-testid={suffixTestId('rank', props)} />
    </SalesLeagueCell>,
    <SalesLeagueCell key="branch">
      <HStack spacing={2} align="center">
        <SalesLeagueBranchBadge
          branchId={props.row.branchId}
          branchCode={props.row.branchCode}
          branchName={props.row.branchName}
          data-testid={suffixTestId('branch', props)}
        />
        <VStack spacing={0}>
          <HStack spacing={2} align="center">
            <Text size="small" alternative={isEmphasised} noWrap>
              {props.row.branchName}
            </Text>
            <Show when={props.row.isCurrentBranch}>
              <Badge
                value={i18n.t('page.salesLeague.branchBoard.table.currentBranch')}
                colorScheme="blue"
                size="small"
                data-testid={suffixTestId('currentBranch', props)}
              />
            </Show>
          </HStack>
          <Text size="xSmall" color="tertiary" noWrap>
            {i18n.t('page.salesLeague.branchBoard.table.repsWithSales', {
              count: props.formatNumber(props.row.sellers) ?? EMPTY_PLACEHOLDER,
            })}
          </Text>
        </VStack>
      </HStack>
    </SalesLeagueCell>,
    <SalesLeagueCell key="rankChange">
      <SalesLeagueRankChange
        trend={props.row.trend}
        data-testid={suffixTestId('rankChange', props)}
      />
    </SalesLeagueCell>,
    <SalesLeagueCell key="vehiclesChange">
      <SalesLeagueVehiclesChange
        vehiclesSoldChange={props.row.vehiclesSoldChange}
        formatNumber={props.formatNumber}
        data-testid={suffixTestId('vehiclesChange', props)}
      />
    </SalesLeagueCell>,
    <SalesLeagueCell key="vehiclesSold">
      <Text size="small" align="right" alternative>
        {props.formatNumber(props.row.vehiclesSold) ?? EMPTY_PLACEHOLDER}
      </Text>
    </SalesLeagueCell>,
    <SalesLeagueCell key="vehiclesPerRep">
      <Text size="small" align="right">
        {isNil(vehiclesPerRep)
          ? EMPTY_PLACEHOLDER
          : (props.formatNumber(vehiclesPerRep, VEHICLES_PER_REP_DECIMALS) ?? EMPTY_PLACEHOLDER)}
      </Text>
    </SalesLeagueCell>,
    <SalesLeagueCell key="gap">
      <Text size="small" align="right" color={isLeader ? 'success' : 'danger'}>
        {gapLabel}
      </Text>
    </SalesLeagueCell>,
    ...(props.marginColumnsVisible
      ? [
          <SalesLeagueCell key="marginPerVehicle">
            <Text size="small" align="right">
              {formatMarginAmount(props.row.marginPerVehicle, props.currency, props.formatCurrency)}
            </Text>
          </SalesLeagueCell>,
          // "0.00" for a branch that sold nothing is a real zero, so it renders as an
          // amount; only a null — margin hidden from this viewer — reaches the placeholder.
          <SalesLeagueCell key="totalMargin">
            {/* The whole column reads bold, not just the emphasised rows — it is the
                figure the board is ultimately about. */}
            <Text size="small" align="right" alternative>
              {formatMarginAmount(props.row.totalMargin, props.currency, props.formatCurrency)}
            </Text>
          </SalesLeagueCell>,
        ]
      : []),
  ];

  return (
    <TableRow
      color={props.row.isCurrentBranch ? CURRENT_BRANCH_ROW_COLOR : undefined}
      hoverColor={ROW_HOVER_COLOR}
      actions={actions}
      actionsWidth={BRANCH_BOARD_ACTIONS_COLUMN_WIDTH}
      data-testid={suffixTestId('row', props)}
    >
      {cells}
    </TableRow>
  );
}
